import { AddBlocksDialog } from '@/features/toolbox/AddBlocksDialog';
import { SKYROVER_COLORS } from '@/theme/skyroverTheme';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { Box, Button } from '@mui/material';
import { useEffect, useState, type RefObject } from 'react';
import { createPortal } from 'react-dom';

interface AddBlocksButtonProps {
  workspaceContainerRef: RefObject<HTMLElement | null>;
}

function findToolbox(container: HTMLElement): HTMLElement | null {
  return container.querySelector('.blocklyToolbox, .blocklyToolboxDiv');
}

export function AddBlocksButton({ workspaceContainerRef }: AddBlocksButtonProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [mountNode, setMountNode] = useState<HTMLElement | null>(null);

  useEffect(() => {
    const container = workspaceContainerRef.current;
    if (!container) {
      return;
    }

    const syncMountNode = (): void => {
      const toolbox = findToolbox(container);
      if (!toolbox) {
        return;
      }

      let footerEl = toolbox.querySelector('.skyrover-add-blocks-footer') as HTMLDivElement | null;
      if (!footerEl) {
        footerEl = document.createElement('div');
        footerEl.className = 'skyrover-add-blocks-footer';
        toolbox.appendChild(footerEl);
      }

      setMountNode(footerEl);
    };

    syncMountNode();

    const observer = new MutationObserver(syncMountNode);
    observer.observe(container, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      findToolbox(container)?.querySelector('.skyrover-add-blocks-footer')?.remove();
      setMountNode(null);
    };
  }, [workspaceContainerRef]);

  if (!mountNode) {
    return null;
  }

  return createPortal(
    <>
      <Box className="skyrover-add-blocks-footer__inner">
        <Button
          fullWidth
          size="small"
          onClick={() => setDialogOpen(true)}
          startIcon={<AddCircleOutlineIcon sx={{ fontSize: 18 }} />}
          sx={{
            justifyContent: 'flex-start',
            textTransform: 'none',
            fontWeight: 700,
            fontSize: 13,
            color: '#333333',
            bgcolor: '#ffffff',
            border: '1px solid #cccccc',
            borderRadius: 1,
            px: 1,
            py: 0.75,
            '&:hover': {
              bgcolor: '#fafafa',
              borderColor: SKYROVER_COLORS.brand,
            },
          }}
        >
          Add blocks
        </Button>
      </Box>

      <AddBlocksDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />
    </>,
    mountNode,
  );
}
